import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';
import { brandColors } from '../../../../theme/theme';
import { TrendingDown, TrendingUp } from '@mui/icons-material';

/**
 * AwarenessFunnel Component
 * 
 * Visualizes the conversion funnel from Total Responses through key stages:
 * Total Responses → Aware → Interest → Theater → Payment
 * 
 * Shows conversion rates at each stage and allows demographic breakdowns
 */
const AwarenessFunnel = ({ data }) => {
  const [selectedEthnicity, setSelectedEthnicity] = useState('all');
  const [selectedAge, setSelectedAge] = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No data available for funnel analysis
        </Typography>
      </Paper>
    );
  }

  // Filter data based on selected demographics
  const filteredData = data.filter(record => {
    if (selectedEthnicity !== 'all' && record.ethnicity !== selectedEthnicity) return false;
    if (selectedAge !== 'all' && record.agegroup !== selectedAge) return false;
    if (selectedGender !== 'all' && record.gender !== selectedGender) return false;
    return true;
  });

  // Calculate funnel metrics
  const totals = filteredData.reduce((acc, record) => ({
    totalResponses: acc.totalResponses + (record.TotalResponses || 0),
    totalAware: acc.totalAware + (record.TotalAware || 0),
    totalInterest: acc.totalInterest + (record.TotalInterest || 0),
    totalTheater: acc.totalTheater + (record.TotalTheater || 0),
    totalWillingToPay: acc.totalWillingToPay + (record.TotalWillingToPay || 0)
  }), {
    totalResponses: 0,
    totalAware: 0,
    totalInterest: 0,
    totalTheater: 0,
    totalWillingToPay: 0
  });

  // Build funnel data
  const funnelData = [
    {
      stage: 'Total Surveyed',
      count: totals.totalResponses,
      percentage: 100,
      color: '#9e9e9e'
    },
    {
      stage: 'Aware',
      count: totals.totalAware,
      percentage: totals.totalResponses > 0 ? (totals.totalAware / totals.totalResponses) * 100 : 0,
      color: brandColors.primary
    },
    {
      stage: 'Interested',
      count: totals.totalInterest,
      percentage: totals.totalResponses > 0 ? (totals.totalInterest / totals.totalResponses) * 100 : 0,
      color: brandColors.secondary
    },
    {
      stage: 'Theater Intent',
      count: totals.totalTheater,
      percentage: totals.totalResponses > 0 ? (totals.totalTheater / totals.totalResponses) * 100 : 0,
      color: '#2196f3'
    },
    {
      stage: 'Will Pay',
      count: totals.totalWillingToPay,
      percentage: totals.totalResponses > 0 ? (totals.totalWillingToPay / totals.totalResponses) * 100 : 0,
      color: '#4caf50'
    }
  ];

  // Calculate conversion rates between stages
  const conversions = [
    {
      from: 'Total Surveyed',
      to: 'Aware',
      rate: totals.totalResponses > 0 ? (totals.totalAware / totals.totalResponses) * 100 : 0,
      dropOff: totals.totalResponses - totals.totalAware
    },
    {
      from: 'Aware',
      to: 'Interested',
      rate: totals.totalAware > 0 ? (totals.totalInterest / totals.totalAware) * 100 : 0,
      dropOff: totals.totalAware - totals.totalInterest
    },
    {
      from: 'Interested',
      to: 'Theater Intent',
      rate: totals.totalInterest > 0 ? (totals.totalTheater / totals.totalInterest) * 100 : 0,
      dropOff: totals.totalInterest - totals.totalTheater
    },
    {
      from: 'Theater Intent',
      to: 'Will Pay',
      rate: totals.totalTheater > 0 ? (totals.totalWillingToPay / totals.totalTheater) * 100 : 0,
      dropOff: totals.totalTheater - totals.totalWillingToPay
    }
  ];

  // Get unique values for filters
  const ethnicities = [...new Set(data.map(r => r.ethnicity).filter(Boolean))];
  const ageGroups = [...new Set(data.map(r => r.agegroup).filter(Boolean))];
  const genders = [...new Set(data.map(r => r.gender).filter(Boolean))];

  // Custom label for bars
  const renderCustomLabel = (props) => {
    const { x, y, width, height, value, index } = props;
    const item = funnelData[index];
    
    return (
      <g>
        <text 
          x={x + width / 2} 
          y={y + height / 2} 
          fill="white" 
          textAnchor="middle" 
          dominantBaseline="middle"
          fontSize="14"
          fontWeight="600"
        >
          {item.count.toLocaleString()}
        </text>
        <text 
          x={x + width / 2} 
          y={y + height / 2 + 18} 
          fill="white" 
          textAnchor="middle" 
          dominantBaseline="middle"
          fontSize="12"
        >
          ({item.percentage.toFixed(1)}%)
        </text>
      </g>
    );
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Awareness & Conversion Funnel
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track audience progression from awareness to payment intent
        </Typography>
      </Box>

      {/* Demographic Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Ethnicity</InputLabel>
            <Select
              value={selectedEthnicity}
              label="Ethnicity"
              onChange={(e) => setSelectedEthnicity(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {ethnicities.map(eth => (
                <MenuItem key={eth} value={eth}>{eth}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Age Group</InputLabel>
            <Select
              value={selectedAge}
              label="Age Group"
              onChange={(e) => setSelectedAge(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {ageGroups.map(age => (
                <MenuItem key={age} value={age}>{age}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Gender</InputLabel>
            <Select
              value={selectedGender}
              label="Gender"
              onChange={(e) => setSelectedGender(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {genders.map(gen => (
                <MenuItem key={gen} value={gen}>{gen}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Funnel Visualization */}
      <Box sx={{ mb: 3 }}>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={funnelData}
            layout="vertical"
            margin={{ top: 20, right: 30, left: 120, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" domain={[0, 100]} unit="%" />
            <YAxis dataKey="stage" type="category" width={110} />
            <RechartsTooltip
              formatter={(value, name, props) => [
                `${props.payload.count.toLocaleString()} (${value.toFixed(1)}%)`,
                'Audience'
              ]}
            />
            <Bar dataKey="percentage" radius={[0, 8, 8, 0]}>
              {funnelData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
              <LabelList content={renderCustomLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Conversion Rate Analysis */}
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Stage Conversion Rates
        </Typography>
        <Grid container spacing={2}>
          {conversions.map((conversion, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card 
                sx={{ 
                  border: conversion.rate >= 50 ? `2px solid ${brandColors.success}40` : `2px solid ${brandColors.warning}40`,
                  backgroundColor: conversion.rate >= 50 ? `${brandColors.success}05` : `${brandColors.warning}05`
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {conversion.from} → {conversion.to}
                    </Typography>
                    {conversion.rate >= 50 ? (
                      <TrendingUp sx={{ color: brandColors.success, fontSize: 20 }} />
                    ) : (
                      <TrendingDown sx={{ color: brandColors.warning, fontSize: 20 }} />
                    )}
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: conversion.rate >= 50 ? brandColors.success : brandColors.warning }}>
                    {conversion.rate.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Drop-off: {conversion.dropOff.toLocaleString()} respondents
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Detailed Table */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Funnel Breakdown
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Stage</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Count</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>% of Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>% of Previous</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {funnelData.map((item, index) => {
                const prevStage = index > 0 ? funnelData[index - 1] : null;
                const pctOfPrevious = prevStage ? (item.count / prevStage.count) * 100 : 100;
                
                return (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box 
                          sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            backgroundColor: item.color 
                          }} 
                        />
                        {item.stage}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {item.count.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {item.percentage.toFixed(1)}%
                    </TableCell>
                    <TableCell align="right">
                      {pctOfPrevious.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Paper>
  );
};

export default AwarenessFunnel;

